import { Amplify } from "aws-amplify";

// Runtime-selectable cell configuration.
//
// The Next.js bundle has *both* cells' Cognito + API endpoints baked in at
// build time (one set of NEXT_PUBLIC_* variables per cell). At runtime we
// pick which cell to talk to based on:
//   1. an explicit user choice persisted in localStorage (set by the
//      region picker on sign-up, or by the cell-lookup result on sign-in),
//   2. the URL `?cell=` query param (handy for testing),
//   3. the configured default cell (US).
//
// Once the active cell is known we lazily configure AWS Amplify against
// that cell's user pool. Switching cells requires reloading the page —
// Amplify's auth context isn't reactive to mid-session reconfigs and the
// SWR cache holds responses keyed by the *previous* API URL.

export type CellId = "us" | "eu";

export const SUPPORTED_CELLS: readonly CellId[] = ["us", "eu"];

const DEFAULT_CELL: CellId = "us";
const LOCAL_STORAGE_KEY = "deflow-cell-id";

export interface CellConfig {
  cellId: CellId;
  label: string;
  apiUrl: string;
  appUrl: string;
  userPoolId: string;
  userPoolClientId: string;
  region: string;
}

// Build-time values, one block per cell. The `?? import.meta.env.VITE_*`
// fallback lets the existing US-only deploy keep working without setting
// per-cell variables; new EU values must be added explicitly.
const env = import.meta.env;

const CELLS: Record<CellId, CellConfig> = {
  us: {
    cellId: "us",
    label: "United States (us-east-1)",
    apiUrl: (env.VITE_API_URL_US ?? env.VITE_API_URL) as string,
    appUrl: (env.VITE_APP_URL_US ?? env.VITE_APP_URL) as string,
    userPoolId: (env.VITE_USER_POOL_ID_US ?? env.VITE_USER_POOL_ID) as string,
    userPoolClientId: (env.VITE_USER_POOL_CLIENT_ID_US ??
      env.VITE_USER_POOL_CLIENT_ID) as string,
    region: "us-east-1",
  },
  eu: {
    cellId: "eu",
    label: "European Union (eu-west-1)",
    apiUrl: env.VITE_API_URL_EU as string,
    appUrl: (env.VITE_APP_URL_EU ?? env.VITE_APP_URL) as string,
    userPoolId: env.VITE_USER_POOL_ID_EU as string,
    userPoolClientId: env.VITE_USER_POOL_CLIENT_ID_EU as string,
    region: "eu-west-1",
  },
};

// Discovery endpoint used *before* a user has a cell pinned. Defaults to the
// US cell because every cell exposes the same `/v1/cell-lookup` route on its
// public API and the global directory is replicated; the choice of which
// cell hosts the discovery call is purely a latency decision.
const LOOKUP_API_URL = (env.VITE_LOOKUP_API_URL ?? CELLS.us.apiUrl) as string;

const isBrowser = () => typeof window !== "undefined";

const fromQueryString = (): CellId | null => {
  if (!isBrowser()) return null;
  const params = new URLSearchParams(window.location.search);
  const value = params.get("cell");
  return value === "us" || value === "eu" ? value : null;
};

const fromLocalStorage = (): CellId | null => {
  if (!isBrowser()) return null;
  try {
    const value = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    return value === "us" || value === "eu" ? value : null;
  } catch {
    return null;
  }
};

export const getActiveCellId = (): CellId => {
  return fromQueryString() ?? fromLocalStorage() ?? DEFAULT_CELL;
};

// Whether the user has explicitly pinned a cell (via the picker or a
// prior sign-in). Used to decide whether to route new users through the
// region picker before sign-up.
export const hasActiveCellId = (): boolean => {
  return fromQueryString() !== null || fromLocalStorage() !== null;
};

export const setActiveCellId = (cellId: CellId): void => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, cellId);
  } catch {
    // localStorage may be disabled (private browsing); cell will fall back
    // to default on next reload, which is fine.
  }
};

export const clearActiveCellId = (): void => {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(LOCAL_STORAGE_KEY);
  } catch {
    // no-op
  }
};

export const cellConfig = (cellId?: CellId): CellConfig => {
  const id = cellId ?? getActiveCellId();
  return CELLS[id];
};

const COGNITO_POOL_ID_RE = /^[a-z]{2}-[a-z]+-\d+_[\w-]+$/;

function isUnsetBuildValue(value: unknown): boolean {
  if (typeof value !== "string") return true;
  const trimmed = value.trim();
  return trimmed === "" || trimmed.startsWith("${");
}

function isValidCognitoPoolId(value: string): boolean {
  return COGNITO_POOL_ID_RE.test(value.trim());
}

/** True when this build has pool + app client IDs for the active cell. */
export function getCellAuthConfigStatus():
  | { ok: true; cell: CellConfig }
  | { ok: false; cell: CellConfig; message: string } {
  const cell = cellConfig();
  const envSuffix = cell.cellId === "eu" ? "EU" : "US";
  const poolId = cell.userPoolId;
  const clientId = cell.userPoolClientId;

  if (isUnsetBuildValue(poolId) || isUnsetBuildValue(clientId)) {
    return {
      ok: false,
      cell,
      message: `Authentication is not configured for ${cell.label}. Set VITE_USER_POOL_ID_${envSuffix} and VITE_USER_POOL_CLIENT_ID_${envSuffix} in the app build.`,
    };
  }

  if (!isValidCognitoPoolId(poolId)) {
    return {
      ok: false,
      cell,
      message: `Authentication is misconfigured for ${cell.label}: VITE_USER_POOL_ID_${envSuffix} must look like "eu-west-1_abc123". Rebuild the app after fixing CI variables.`,
    };
  }

  return { ok: true, cell };
}

export const allCellConfigs = (): readonly CellConfig[] => [
  CELLS.us,
  CELLS.eu,
];

let amplifyConfiguredFor: CellId | null = null;

// Lazy, idempotent Amplify.configure call. Re-configures only when the
// active cell changes from a prior call within the same page load (rare —
// callers typically reload after switching cells).
export const configureAmplifyForActiveCell = (): CellConfig => {
  const config = cellConfig();
  if (amplifyConfiguredFor === config.cellId) return config;

  const authReady = getCellAuthConfigStatus();
  if (!authReady.ok) {
    if (typeof console !== "undefined") {
      console.warn(
        `cell.configureAmplifyForActiveCell: ${authReady.message}`
      );
    }
    return config;
  }

  Amplify.configure(
    {
      Auth: {
        Cognito: {
          userPoolId: config.userPoolId.trim(),
          userPoolClientId: config.userPoolClientId.trim(),
        },
      },
    },
    { ssr: true }
  );
  amplifyConfiguredFor = config.cellId;
  return config;
};

// Calls GET /v1/cell-lookup against the discovery URL and returns the
// matched cell, or null if the email isn't in the directory yet (e.g. the
// user hasn't signed up, or sign-up just happened and replication hasn't
// caught up).
export const lookupCellByEmail = async (
  email: string,
  signal?: AbortSignal
): Promise<CellId | null> => {
  const trimmed = email.trim().toLowerCase();
  if (!trimmed || !LOOKUP_API_URL) return null;

  const url = `${LOOKUP_API_URL}/v1/cell-lookup?email=${encodeURIComponent(
    trimmed
  )}`;
  try {
    const res = await fetch(url, { signal });
    if (!res.ok) return null;
    const body = (await res.json()) as { cellId?: CellId | null };
    return body.cellId === "us" || body.cellId === "eu" ? body.cellId : null;
  } catch {
    return null;
  }
};

export type CellResolutionSource = "lookup" | "existing";

/** Pins the cell from the directory when found; otherwise keeps the active cell. */
export const resolveCellForEmail = async (
  email: string,
  signal?: AbortSignal
): Promise<{ cellId: CellId; source: CellResolutionSource }> => {
  const lookedUp = await lookupCellByEmail(email, signal);
  if (lookedUp) {
    setActiveCellId(lookedUp);
    return { cellId: lookedUp, source: "lookup" };
  }
  return { cellId: getActiveCellId(), source: "existing" };
};
